import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { errorMessage } from './errors'
import { http } from './api'
import type {
  DashboardResponse,
  ViewScope,
  HoursReportDetail,
  PersonalDashboard,
  HoursReportHistoryRow,
  ReportSettingsInput,
  ReportsOverview,
  ReportsSendResult,
  ProjectSendResult,
} from './report-types'

export interface ReportApi {
  dashboard(year: number): Promise<DashboardResponse>
  personalDashboard(year: number, personId?: string | null): Promise<PersonalDashboard>
  reportsOverview(): Promise<ReportsOverview>
  hoursReport(clientId: string, projectId: string, fromDate?: string | null): Promise<HoursReportDetail>
  sendHoursReport(clientId: string, projectId?: string | null, fromDate?: string | null): Promise<ReportsSendResult>
  updateReportSettings(clientId: string, input: ReportSettingsInput): Promise<ReportsOverview>
  reportHistory(clientId: string, projectId?: string | null): Promise<HoursReportHistoryRow[]>
}

class RealReportApi implements ReportApi {
  dashboard(year: number) {
    return http.get<DashboardResponse>('/api/reports/dashboard', { year, scope: 'company' })
  }
  personalDashboard(year: number, personId?: string | null) {
    return http.get<PersonalDashboard>('/api/reports/dashboard', { year, scope: 'personal', personId: personId ?? undefined })
  }
  reportsOverview() {
    return http.get<ReportsOverview>('/api/reports/hours')
  }
  hoursReport(clientId: string, projectId: string, fromDate?: string | null) {
    return http.get<HoursReportDetail>('/api/reports/hours', { clientId, projectId, fromDate: fromDate ?? undefined })
  }
  sendHoursReport(clientId: string, projectId?: string | null, fromDate?: string | null) {
    return http.post<ReportsSendResult>('/api/reports/hours/send', { clientId, projectId: projectId ?? undefined, fromDate: fromDate ?? undefined })
  }
  updateReportSettings(clientId: string, input: ReportSettingsInput) {
    return http.patch<ReportsOverview>(`/api/reports/clients/${clientId}/settings`, input)
  }
  async reportHistory(clientId: string, projectId?: string | null) {
    const res = await http.get<{ reports: HoursReportHistoryRow[] }>('/api/reports/hours/history', { clientId, projectId: projectId ?? undefined })
    return res.reports
  }
}

export const reportApi: ReportApi = new RealReportApi()

export async function downloadLedger(year: number, format: 'json' | 'csv' = 'csv'): Promise<void> {
  const res = await fetch(`/api/reports/ledger?year=${year}&format=${format}`, { credentials: 'include' })
  if (!res.ok) {
    toast.error('Ledger-export mislukt')
    return
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `grootboek-${year}.${format}`
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

export const reportKeys = {
  dashboard: (year: number, scope: ViewScope, personId?: string | null) =>
    ['reports', 'dashboard', scope, year, personId ?? 'self'] as const,
  overview: ['reports', 'overview'] as const,
  hours: (clientId: string, projectId: string, fromDate?: string | null) => ['reports', 'hours', clientId, projectId, fromDate ?? 'default'] as const,
  history: (clientId: string, projectId?: string | null) => ['reports', 'history', clientId, projectId ?? 'all'] as const,
}

export function useDashboard(year: number, enabled = true) {
  return useQuery({ queryKey: reportKeys.dashboard(year, 'company'), queryFn: () => reportApi.dashboard(year), enabled })
}

export function usePersonalDashboard(year: number, personId?: string | null, enabled = true) {
  return useQuery({
    queryKey: reportKeys.dashboard(year, 'personal', personId),
    queryFn: () => reportApi.personalDashboard(year, personId),
    enabled,
  })
}

export function useReportsOverview() {
  return useQuery({ queryKey: reportKeys.overview, queryFn: () => reportApi.reportsOverview() })
}

export function useHoursReport(clientId: string | null, projectId: string | null, fromDate?: string | null) {
  return useQuery({
    queryKey: reportKeys.hours(clientId ?? '', projectId ?? '', fromDate),
    queryFn: () => reportApi.hoursReport(clientId as string, projectId as string, fromDate),
    enabled: !!clientId && !!projectId,
  })
}

/**
 * Een ronde over alle projecten stopt niet meer bij het eerste project dat
 * strandt, dus "de aanroep is geslaagd" is niet langer hetzelfde als "alles is
 * verzonden". Zonder deze afweging meldt de knop groen succes terwijl er niets
 * de deur uit ging.
 *
 * `unrecorded` staat apart van `failed` omdat de klant zijn rapport dan wel
 * heeft: het vastleggen strandde, dus die uren gelden nog als niet gerapporteerd
 * en komen bij een volgende ronde opnieuw mee. Dat vraagt iets anders van de
 * lezer dan een mail die nooit verstuurd is.
 */
function reportSendToast(projects: ProjectSendResult[] | undefined, projectId?: string | null): void {
  if (undefined === projects) {
    toast.success(projectId ? 'Rapport verzonden' : 'Alle projectrapporten verzonden')
    return
  }

  const names = (rows: ProjectSendResult[]) => rows.map((p) => p.projectName).join(', ')
  const sent = projects.filter((p) => 'sent' === p.status)
  const failed = projects.filter((p) => 'failed' === p.status)
  const unrecorded = projects.filter((p) => 'unrecorded' === p.status)

  if (failed.length > 0) {
    toast.warning(`${sent.length} verzonden, ${failed.length} mislukt`, {
      description: `Niet verzonden: ${names(failed)}. ${failed[0].error ?? ''}`.trim(),
      duration: 10000,
    })
  } else if (sent.length > 0) {
    toast.success(`${sent.length} ${1 === sent.length ? 'projectrapport' : 'projectrapporten'} verzonden`)
  }

  if (unrecorded.length > 0) {
    toast.warning(`${unrecorded.length} verzonden maar niet vastgelegd`, {
      description: `${names(unrecorded)}: de klant heeft het rapport, maar deze uren gelden nog als niet gerapporteerd en komen een volgende keer opnieuw mee.`,
      duration: 10000,
    })
  }
}

export function useSendHoursReport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ clientId, projectId, fromDate }: { clientId: string; projectId?: string | null; fromDate?: string | null }) =>
      reportApi.sendHoursReport(clientId, projectId, fromDate),
    onSuccess: ({ projects, ...overview }, { clientId, projectId }) => {
      queryClient.setQueryData(reportKeys.overview, overview)
      queryClient.invalidateQueries({ queryKey: ['reports', 'history', clientId] })
      queryClient.invalidateQueries({ queryKey: ['reports', 'hours', clientId] })
      reportSendToast(projects, projectId)
    },
    onError: (e) => toast.error(errorMessage(e, 'Versturen mislukt')),
  })
}

export function useUpdateReportSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ clientId, input }: { clientId: string; input: ReportSettingsInput }) =>
      reportApi.updateReportSettings(clientId, input),
    onSuccess: (overview) => {
      queryClient.setQueryData(reportKeys.overview, overview)
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      toast.success('Rapportage-instellingen opgeslagen')
    },
    onError: (e) => toast.error(errorMessage(e, 'Opslaan mislukt')),
  })
}

export function useReportHistory(clientId: string | null, projectId?: string | null) {
  return useQuery({
    queryKey: reportKeys.history(clientId ?? '', projectId),
    queryFn: () => reportApi.reportHistory(clientId as string, projectId),
    enabled: !!clientId,
  })
}
